import React from "react";
import type { TransformedCartItem } from "../../../cart/types/cartApiTypes";

interface CheckoutItemRowProps {
  item: TransformedCartItem;
  currencySymbol: string;
}

const CheckoutItemRow: React.FC<CheckoutItemRowProps> = ({
  item,
  currencySymbol,
}) => {
  return (
    <div className="order-summary-item">
      <div className="item-image">
        <img
          src={item.images?.[0]?.thumbnail || ""}
          alt={item.name}
          width="60" // Consistent size
          height="60"
        />
      </div>

      {/* --- Refactored item-details section --- */}
      <div className="item-details">
        {/* Product Name and Quantity */}
        <span className="item-name">
          {item.name} × {item.quantity}
        </span>

        {/* Display enriched variation attributes */}
        {item.attributes.length > 0 && (
          <div className="checkout-item-attributes">
            {item.attributes.map((attribute) => {
              // The 'term' property holds the single selected term's enriched data
              const term = attribute.term;
              // Use the attribute's displayType, default to button-text if missing
              const displayType = attribute.displayType || "button-text";

              // Skip rendering if term data is somehow missing (shouldn't happen with proper transforms)
              if (!term) return null;

              return (
                <div
                  key={attribute.id}
                  className={`checkout-item-attribute type-${displayType}`}
                >
                  {/* Display Attribute Name */}
                  <span className="attribute-name">{attribute.name}:</span>

                  {/* Display Attribute Value based on type */}
                  {(() => {
                    switch (displayType) {
                      case "color":
                        return (
                          <span
                            className="checkout-item-color-swatch"
                            style={{
                              backgroundColor: term.color || "#CCCCCC", // Use term color
                            }}
                            title={term.name} // Show term name on hover
                            aria-label={`${attribute.name}: ${term.name}`} // Accessibility
                          ></span>
                        );
                      case "button-text":
                        return (
                          <span className="checkout-item-text-button">
                            {term.name} {/* Display term name */}
                          </span>
                        );
                      case "button-image":
                        return term.buttonImage?.thumbnail ? ( // Check if image data exists
                          <img
                            src={term.buttonImage.thumbnail}
                            alt={term.name}
                            className="checkout-item-image-preview"
                            title={term.name}
                            width="20" // Specify small dimensions
                            height="20"
                          />
                        ) : (
                          // Fallback to text if image is missing for some reason
                          <span className="checkout-item-text-button">
                            {term.name}
                          </span>
                        );
                      default:
                        // Fallback for unknown types or simple text display
                        return <span>{term.name}</span>;
                    }
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* --- End of Refactored Section --- */}

      <div className="item-total">
        {item.totals.formattedLineSubtotal}
        {currencySymbol}
      </div>
    </div>
  );
};

export default CheckoutItemRow;
